import React, { useMemo, useState } from 'react';
import { Plus, Shield, X, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  getAddableExtraPermissions,
  permissionLabel,
} from '../utils/rolePermissions';

export default function TeacherPermissionsCard({
  permissions,
  canEdit = false,
  saving = false,
  onAdd,
  onRemove,
}) {
  const [open, setOpen] = useState(false);

  const addable = useMemo(
    () => getAddableExtraPermissions(permissions?.role_permissions, permissions?.extra_permissions),
    [permissions]
  );

  if (!permissions?.can_have_extra && !permissions?.role_permissions?.length) {
    return null;
  }

  const rolePerms = permissions?.role_permissions || [];
  const extraPerms = permissions?.extra_permissions || [];

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-foreground flex items-center gap-2 text-base">
          <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" /> Permissions
        </CardTitle>
        <CardDescription>
          Default from <span className="font-medium text-foreground">{permissions?.role_name || 'role'}</span>.
          {canEdit && permissions?.can_have_extra
            ? ' Add extra permissions for this user only.'
            : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">From role</p>
          <div className="flex flex-wrap gap-1.5">
            {rolePerms.length === 0 ? (
              <span className="text-sm text-muted-foreground">No role permissions</span>
            ) : (
              rolePerms.map((p) => (
                <span
                  key={p}
                  className="px-2 py-1 rounded-md bg-muted text-xs text-foreground border border-border"
                >
                  {permissionLabel(p)}
                </span>
              ))
            )}
          </div>
        </div>

        {permissions?.can_have_extra && (
          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Extra permissions</p>
              {canEdit && addable.length > 0 && (
                <DropdownMenu open={open} onOpenChange={setOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 rounded-lg border-border"
                      disabled={saving}
                    >
                      {saving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <>
                          <Plus className="h-3.5 w-3.5 mr-1" /> Add
                        </>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto w-56">
                    {addable.map((key) => (
                      <DropdownMenuItem
                        key={key}
                        className="cursor-pointer text-sm"
                        onClick={() => {
                          setOpen(false);
                          onAdd?.(key);
                        }}
                      >
                        {permissionLabel(key)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {extraPerms.length === 0 ? (
                <span className="text-sm text-muted-foreground">No extra permissions assigned</span>
              ) : (
                extraPerms.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-500/10 text-blue-700 dark:text-blue-300 text-xs border border-blue-500/20"
                  >
                    {permissionLabel(p)}
                    {canEdit && (
                      <button
                        type="button"
                        className="rounded hover:bg-blue-500/20 p-0.5"
                        disabled={saving}
                        onClick={() => onRemove?.(p)}
                        aria-label={`Remove ${permissionLabel(p)}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
